/**
 * Combo Calculator
 *
 * Calculates the number of remaining combinations for each hand
 * given a set of dead cards.
 *
 * Base combos (no dead cards):
 * - Pairs: 6 combos (C(4,2) = 6 ways to pick 2 suits from 4)
 * - Suited: 4 combos (one per suit)
 * - Offsuit: 12 combos (4×4 = 16 total minus 4 suited = 12)
 */

const SUITS = ['c', 'd', 'h', 's'] as const;

/**
 * Get available suits for a rank given dead cards
 */
function getAvailableSuits(rank: string, deadCards: Set<string>): string[] {
  return SUITS.filter((suit) => !deadCards.has(`${rank}${suit}`));
}

/**
 * Calculate remaining pair combos for a rank
 * Pairs: C(available, 2) = available * (available - 1) / 2
 */
export function getPairCombos(rank: string, deadCards: Set<string>): number {
  const available = getAvailableSuits(rank, deadCards).length;
  if (available < 2) return 0;
  return (available * (available - 1)) / 2;
}

/**
 * Calculate remaining suited combos for two ranks
 * Suited: count suits where both ranks are available
 */
export function getSuitedCombos(rank1: string, rank2: string, deadCards: Set<string>): number {
  let count = 0;
  for (const suit of SUITS) {
    const card1 = `${rank1}${suit}`;
    const card2 = `${rank2}${suit}`;
    if (!deadCards.has(card1) && !deadCards.has(card2)) {
      count++;
    }
  }
  return count;
}

/**
 * Calculate remaining offsuit combos for two ranks
 * Offsuit: count all rank1-suit × rank2-suit where suits differ
 */
export function getOffsuitCombos(rank1: string, rank2: string, deadCards: Set<string>): number {
  const available1 = getAvailableSuits(rank1, deadCards);
  const available2 = getAvailableSuits(rank2, deadCards);

  let count = 0;
  for (const suit1 of available1) {
    for (const suit2 of available2) {
      if (suit1 !== suit2) {
        count++;
      }
    }
  }
  return count;
}

/**
 * Get the type of a hand from its notation
 */
function getHandType(hand: string): 'pair' | 'suited' | 'offsuit' {
  if (hand.length === 2) {
    return 'pair'; // e.g., "AA"
  }
  const suffix = hand[2];
  if (suffix === 's') return 'suited';
  if (suffix === 'o') return 'offsuit';
  return 'pair'; // fallback
}

/**
 * Get ranks from a hand notation
 */
function getHandRanks(hand: string): [string, string] {
  return [hand[0], hand[1]];
}

/**
 * Calculate remaining combos for a hand given dead cards
 *
 * @param hand - Hand notation (e.g., "AA", "AKs", "72o")
 * @param deadCards - Set of dead cards (e.g., {"Ac", "Kh"})
 * @returns Number of remaining combinations (0-12)
 */
export function getRemainingCombos(hand: string, deadCards: Set<string>): number {
  const type = getHandType(hand);
  const [rank1, rank2] = getHandRanks(hand);

  switch (type) {
    case 'pair':
      return getPairCombos(rank1, deadCards);
    case 'suited':
      return getSuitedCombos(rank1, rank2, deadCards);
    case 'offsuit':
      return getOffsuitCombos(rank1, rank2, deadCards);
  }
}

/**
 * Calculate the base combos for a hand type (no dead cards)
 */
export function getBaseCombos(type: 'pair' | 'suited' | 'offsuit'): number {
  switch (type) {
    case 'pair':
      return 6;
    case 'suited':
      return 4;
    case 'offsuit':
      return 12;
  }
}

/**
 * Calculate all 169 hand combos with dead cards applied
 * Returns a 13x13 grid matching the standard hand matrix layout
 */
export function calculateComboGrid(deadCards: Set<string>): number[][] {
  const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
  const grid: number[][] = [];

  for (let row = 0; row < 13; row++) {
    const rowData: number[] = [];
    for (let col = 0; col < 13; col++) {
      const rank1 = RANKS[row];
      const rank2 = RANKS[col];

      if (row === col) {
        // Diagonal = pairs
        rowData.push(getPairCombos(rank1, deadCards));
      } else if (col > row) {
        // Upper triangle = suited
        rowData.push(getSuitedCombos(rank1, rank2, deadCards));
      } else {
        // Lower triangle = offsuit
        rowData.push(getOffsuitCombos(rank1, rank2, deadCards));
      }
    }
    grid.push(rowData);
  }

  return grid;
}
